import React, { useEffect, useState, useRef } from "react";
import { Terminal, Wifi, Clock, Layers, Activity } from "lucide-react";

const LiveTerminal: React.FC = () => {
  const [logs, setLogs] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<string>("RADIUS"); // Default tab
  const [status, setStatus] = useState<string>("CONNECTING");
  const wsRef = useRef<WebSocket | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 1. Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    setLogs([]); // Clear screen on tab switch
    setStatus("CONNECTING");

    // 2. Open new connection for the selected service
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    // Matches the API route we created in Step 3
    const wsUrl = `${protocol}//${window.location.hostname}/api/v1/ws/live-logs/${activeTab}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus("LIVE");
      setLogs((prev) => [...prev, `>>> ATTACHED TO CONTAINER: ${activeTab}`]);
    };

    ws.onmessage = (event) => {
      // Docker logs might come in chunks, split by newline
      const newLines = event.data
        .split("\n")
        .filter((line: string) => line.trim() !== "");
      setLogs((prev) => [...prev, ...newLines].slice(-500)); // Keep last 500 lines
    };

    ws.onclose = () => setStatus("DISCONNECTED");
    ws.onerror = () => setStatus("ERROR");

    // Cleanup when component unmounts or tab changes
    return () => {
      ws.close();
    };
  }, [activeTab]);

  // Auto-scroll
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  return (
    <div className="bg-black text-gray-300 font-mono text-xs md:text-sm rounded-lg shadow-2xl overflow-hidden border border-gray-800 w-full max-w-6xl mx-auto mt-6">
      {/* Header & Tabs */}
      <div className="bg-gray-900 border-b border-gray-800">
        <div className="px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Terminal className="w-5 h-5 text-green-400" />
            <span className="font-bold text-white tracking-widest">
              OPS TERMINAL v1.0
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Activity
              className={`w-4 h-4 ${
                status === "LIVE"
                  ? "text-green-500 animate-pulse"
                  : "text-red-500"
              }`}
            />
            <span className="text-[10px] font-bold text-gray-400">
              {status}
            </span>
          </div>
        </div>

        {/* Tab Bar */}
        <div className="flex bg-gray-950 border-b border-gray-800">
          {[
            { id: "RADIUS", icon: Wifi, label: "RADIUS SERVER" },
            { id: "SCHEDULER", icon: Clock, label: "JOB SCHEDULER" },
            { id: "APP", icon: Layers, label: "BACKEND API" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 text-[11px] font-bold tracking-wider transition-all border-r border-gray-800 hover:bg-gray-900 ${
                activeTab === tab.id
                  ? "text-green-400 bg-gray-900 border-b-2 border-b-green-500"
                  : "text-gray-600 border-b-transparent"
              }`}
            >
              <tab.icon className="w-3 h-3" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Terminal Output */}
      <div className="h-[500px] overflow-y-auto p-4 bg-[#0c0c0c] space-y-0.5">
        {logs.map((line, i) => (
          <div key={i} className="break-all hover:bg-white/5 px-1 rounded">
            {/* Simple syntax highlighting based on content */}
            <span
              className={`
              ${
                line.toLowerCase().includes("error") || line.includes("❌")
                  ? "text-red-400 font-bold"
                  : ""
              }
              ${line.toLowerCase().includes("warn") ? "text-yellow-400" : ""}
              ${line.includes(">>>") ? "text-blue-400 font-bold" : ""}
              ${
                !line.includes("error") &&
                !line.includes("warn") &&
                !line.includes(">>>")
                  ? "text-gray-300"
                  : ""
              }
            `}
            >
              {line}
            </span>
          </div>
        ))}
        <div ref={endRef} />
      </div>
    </div>
  );
};

export default LiveTerminal;
